// src/pages/LoginPage.jsx
import React, { useCallback, useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useLocation } from "react-router-dom";
import { clearAdminOrganizationView } from "../utils/adminOrganizationView";

export default function LoginPage() {
  const location = useLocation();
  const requestedCreatorPath = location.state?.from?.pathname?.startsWith("/creator")
    ? location.state.from.pathname
    : null;
  const [email, setEmail] = useState("");

  // one-Password flow
  const [password, setPassword] = useState("");

  // two-Password flow (for unregistered email)
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [displayName, setDisplayName] = useState("");

  // UI state
  const [stage, setStage] = useState("email"); // "email" | "one" | "two" | "profile"
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [orgId, setOrgId] = useState("");

  const [newOrgMode, setNewOrgMode] = useState(false); // 👈 NEW

  const [infotext, setInfotext] = useState("Registriere oder logge dich ein");

  const redirectToDefaultArea = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/me`, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) {
        window.location.href = "/übersicht";
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (data?.can_access_admin) {
        window.location.href = "/admin";
      } else if (requestedCreatorPath && data?.can_manage_blog) {
        window.location.href = requestedCreatorPath;
      } else if (data?.role === "content_creator") {
        window.location.href = "/creator";
      } else {
        window.location.href = data?.role === "family_member" ? "/family" : "/übersicht";
      }
    } catch {
      window.location.href = "/übersicht";
    }
  }, [requestedCreatorPath]);


  // 👇 Add this useEffect
  useEffect(() => {
    async function checkAuth() {
      clearAdminOrganizationView();
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/status`, {
          method: "GET",
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          if (data.authenticated) {
            await redirectToDefaultArea();
          }
        }
      } catch (err) {
        console.warn("Auth check failed:", err);
      }
    }

    checkAuth();
  }, [redirectToDefaultArea]);


  async function onSubmit(e) {
    e.preventDefault();
    setErr("");

    // 1) First click: check email on backend
    if (stage === "email") {
      if (!email) {
        setErr("Bitte E-Mail eingeben.");
        return;
      }
      try {
          console.log(`${process.env.REACT_APP_API_URL}/login`);
        setLoading(true);
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/login`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email }),
          }
        );
        if (res.ok) {
          setStage("one");
          setInfotext("logge dich ein")
        } else if (res.status === 404) {
          setStage("two");
          setInfotext("Kein Account gefunden. Erstelle einen Account")
        } else {
          const data = await res.json().catch(() => ({}));
          setErr(data.message || "Unerwartete Antwort vom Server.");
        }
      } catch (e) {
        setErr("Netzwerkfehler – bitte später erneut versuchen.");
      } finally {
        setLoading(false);
      }
      return;
    }

    if (stage === "one") {
      if (!email || !password) {
        setErr("Bitte E-Mail und Passwort eingeben.");
        return;
      }
      try {
        setLoading(true);
        const res = await fetch(`${process.env.REACT_APP_API_URL}/login/verify`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        });
        const data = await res.json().catch(() => ({}));

        if (res.ok && data.requires_super_admin_password) {
          setStage("superadmin");
          setInfotext("Lege dein persönliches Super-Admin-Passwort fest");
        } else if (res.ok && data.authenticated === true) {
          if (data.needs_org) {
            // hide email/password, show org selector
            setStage("org");
            setInfotext("Gebe den Namen deiner Organisation ein oder erstelle eine neue Organisation")

          } else {
            await redirectToDefaultArea();
          }
        } else if (res.status === 401) {
          setErr(data.message || "E-Mail oder Passwort ist falsch.");
        } else {
          setErr(data.message || "Login fehlgeschlagen.");
        }
      } catch {
        setErr("Netzwerkfehler – bitte später erneut versuchen.");
      } finally {
        setLoading(false);
      }
      return;
    }
    if (stage === "superadmin") {
      if (!password1 || password1 !== password2) {
        setErr("Die Passwörter müssen übereinstimmen.");
        return;
      }
      try {
        setLoading(true);
        const res = await fetch(`${process.env.REACT_APP_API_URL}/super-admin/setup`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password: password1 }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.authenticated) await redirectToDefaultArea();
        else setErr(data.message || "Super-Admin konnte nicht erstellt werden.");
      } catch {
        setErr("Netzwerkfehler – bitte später erneut versuchen.");
      } finally {
        setLoading(false);
      }
      return;
    }
   if (stage === "org") {
  if (!orgId) {
    setErr("Bitte einen Organisationsnamen eingeben.");
    return;
  }
  try {
    setLoading(true);
    if (newOrgMode) {
      // CREATE NEW ORG + ASSIGN USER (via session)
      const res = await fetch(`${process.env.REACT_APP_API_URL}/organizations/create`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ organization_name: orgId }), // ❌ no email here
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.ok) {
        await redirectToDefaultArea();
      } else {
        setErr(data.message || "Organisation konnte nicht erstellt werden.");
      }
    } else {
      // ASSIGN EXISTING ORG BY NAME
      const res = await fetch(`${process.env.REACT_APP_API_URL}/login/verify-organization`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ organization_name: orgId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) await redirectToDefaultArea();
      else setErr(data.message || "Organisation konnte nicht gesetzt werden.");
    }
  } catch {
    setErr("Netzwerkfehler – bitte später erneut versuchen.");
  } finally {
    setLoading(false);
  }
  return;
}
    if (stage === "two") {
      if (!password1 || !password2) {
        setErr("Bitte beide Passwörter eingeben.");
        return;
      }
      if (password1 !== password2) {
        setErr("Passwörter stimmen nicht überein.");
        return;
      }
      setStage("profile");
      setInfotext("Vervollständige dein Profil");
      return;
    }

    if (stage === "profile") {
      if (!firstName.trim() || !lastName.trim()) {
        setErr("Bitte Vorname und Nachname eingeben.");
        return;
      }

      try {
        setLoading(true);
        const res = await fetch(`${process.env.REACT_APP_API_URL}/signup`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            password: password1,
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            display_name: displayName.trim() || null,
          }),
        });

        const data = await res.json().catch(() => ({}));

        if (res.ok && data.authenticated) {
          // ✅ session is set on the server, user is logged in
          if (data.needs_org) {
            setStage("org");
            setNewOrgMode(false); // default to selecting or creating
            setInfotext("Gebe den Namen deiner Organisation ein oder erstelle eine neue Organisation");
          } else {
            await redirectToDefaultArea();
          }
        } else {
          setErr(data.message || "Registrierung fehlgeschlagen.");
        }
      } catch (e) {
        setErr("Netzwerkfehler – bitte später erneut versuchen.");
      } finally {
        setLoading(false);
      }
      return;
    }
  }

  function handleCreateOrg() {
    setNewOrgMode(true); // 👈 hide the button and switch Weiter to "create org" behavior
    setInfotext("Gebe einen Namen für deine neue Organisation ein")

  }


  return (
    <div className="flex min-h-screen flex-col bg-[#DEF1FF]">
      <Header />

      <main className="flex grow items-center justify-center">
        <section className="flex w-full max-w-md flex-col items-center px-4 text-center">
          <h1 className="mb-2 text-3xl font-extrabold tracking-tight text-gray-900">
            SteffiCare
          </h1>
          <p className="mb-6 text-xs text-gray-600">
            {infotext}
          </p>
          {/* EMAIL and PASSWORD */}
          <form onSubmit={onSubmit} className="w-full text-left">
            <AnimatePresence>
              {(stage === "email" || stage === "one" || stage === "two") && (
                <motion.div
                  key="creds"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                >
                  <div className="mb-3">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setErr("");
                        if (stage !== "email") setStage("email");
                      }}
                      placeholder="Email-Adresse"
                      className="w-full rounded-full border border-black/5 bg-white px-5 py-3 text-sm text-gray-900 shadow-sm outline-none placeholder:text-gray-400 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-200"
                    />
                  </div>

                  {stage === "one" && (
                    <div className="mb-4">
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Passwort"
                        className="w-full rounded-full border border-black/5 bg-white px-5 py-3 text-sm text-gray-900 shadow-sm outline-none placeholder:text-gray-400 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-200"
                      />
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {stage === "profile" && (
                <motion.div
                  key="profile"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  className="mb-4 space-y-3"
                >
                  <input
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Vorname"
                    autoComplete="given-name"
                    className="w-full rounded-full border border-black/5 bg-white px-5 py-3 text-sm text-gray-900 shadow-sm outline-none placeholder:text-gray-400 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-200"
                  />
                  <input
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Nachname"
                    autoComplete="family-name"
                    className="w-full rounded-full border border-black/5 bg-white px-5 py-3 text-sm text-gray-900 shadow-sm outline-none placeholder:text-gray-400 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-200"
                  />
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Name, mit dem Bewohner dich ansprechen (optional)"
                    autoComplete="nickname"
                    className="w-full rounded-full border border-black/5 bg-white px-5 py-3 text-sm text-gray-900 shadow-sm outline-none placeholder:text-gray-400 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-200"
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* PASSWORD (two fields) */}
            <AnimatePresence>
              {(stage === "two" || stage === "superadmin") && (
                <motion.div
                  key="pw-two"
                  initial={{ opacity: 0, scaleY: 0.6, y: -8, transformOrigin: "top" }}
                  animate={{ opacity: 1, scaleY: 1, y: 0 }}
                  exit={{ opacity: 0, scaleY: 0.6, y: -8 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="space-y-3 mb-4"
                >
                  <input
                    type="password"
                    value={password1}
                    onChange={(e) => setPassword1(e.target.value)}
                    placeholder={stage === "superadmin" ? "Neues Super-Admin-Passwort" : "Passwort"}
                    className="w-full rounded-full border border-black/5 bg-white px-5 py-3 text-sm text-gray-900 shadow-sm outline-none ring-0 placeholder:text-gray-400 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                  <input
                    type="password"
                    value={password2}
                    onChange={(e) => setPassword2(e.target.value)}
                    placeholder="Passwort bestätigen"
                    className="w-full rounded-full border border-black/5 bg-white px-5 py-3 text-sm text-gray-900 shadow-sm outline-none ring-0 placeholder:text-gray-400 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                </motion.div>
              )}
            </AnimatePresence>
      
            {/* ORG SELECT (only when needs_org) */}
            <AnimatePresence>
              {stage === "org" && (
                <motion.div
                  key="org"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                >
                  {/* Organisation input stays in same place */}
                  <div className="mb-3">
                    <input
                      type="text"
                      value={orgId}
                      onChange={(e) => setOrgId(e.target.value)}
                      placeholder="Organisation Name"
                      className="w-full rounded-full border border-black/5 bg-white px-5 py-3 text-sm text-gray-900 shadow-sm outline-none placeholder:text-gray-400 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-200"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Submit */}
            <button
              type="submit"
              className="w-full rounded-full bg-[#76B3FF] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:brightness-105 active:translate-y-px disabled:opacity-50"
              disabled={loading}
            >
              {stage === "email"
                ? (loading ? "Prüfe…" : "Weiter")
                : stage === "profile"
                  ? (loading ? "Erstelle Account…" : "Account erstellen")
                  : "Weiter"}
            </button>

            {/* Neue Organisation button BELOW Weiter, only in org stage */}
            {stage === "org" && !newOrgMode && (
              <button
                type="button"
                onClick={handleCreateOrg}
                className="mt-3 w-full rounded-full bg-[#282626] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:brightness-105 active:translate-y-px"
              >
                Neue Organisation
              </button>
            )}

            {err && (
              <p className="mt-2 text-center text-sm text-red-600">{err}</p>
            )}
          </form>
        </section>
      </main>

      <Footer />
    </div>
  );
}
